import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAuthentication } from '../../hooks/useAuthentication'
import { useQuery } from '@apollo/react-hooks'
import {
  GET_VOUCHERS_ALL,
  GET_FINANCIAL_YEARS,
  GET_DEPARTMENTS,
  GET_CATEGORIES,
} from '../Vouchers/gql'
import { DataTable } from '../table/DataTable'
import { getReportsColumns } from '../table/columns/reportsColumns'
import {
  Box,
  Grid2 as Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import AssessmentIcon from '@mui/icons-material/Assessment'
import Loader from '../Loader'
import PageHeader from '../Common/PageHeader'
import VoucherExpandedRow from '../table/VoucherExpandedRow'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { GET_TAGS } from '../../api/tag'
import TagFilter from '../Tags/TagFilter'
import { tagIdFromSearch } from '../Tags/tagLinks'
import { PAYMENT_ACCOUNT_LABELS, PaymentAccount, Tag } from '../../types'
import {
  hasReportsAccess,
  hasFullReportsAccess,
  findCurrentFinancialYear,
  getFinancialYearPeriods,
} from '../../utils'
import { Roles } from '../../types'

const Reports = () => {
  const { authState } = useAuthentication()
  const userBranch = authState.authUser.branch
  const isNational = userBranch === 'NATIONAL'
  const isAllowed = hasReportsAccess(
    userBranch,
    authState.authUser.department?.name,
    authState.authUser.user_role?.code,
  )
  const hasFullAccess = hasFullReportsAccess(
    authState.authUser.department?.name,
  )
  const userDepartmentId = authState.authUser.department_id
  const isNationalMaal = authState.authUser.role_id === Roles.NATIONAL.MAAL.id

  const financialYearsResult = useQuery(GET_FINANCIAL_YEARS, {
    context: { headers: { authorization: authState.authToken } },
  })
  const departmentsResult = useQuery(GET_DEPARTMENTS, {
    context: { headers: { authorization: authState.authToken } },
  })
  const categoriesResult = useQuery(GET_CATEGORIES, {
    context: { headers: { authorization: authState.authToken } },
  })
  const sortedCostCategories = useMemo(
    () =>
      [...(categoriesResult.data?.costCategories ?? [])].sort(
        (a: any, b: any) => a.name.localeCompare(b.name),
      ),
    [categoriesResult.data],
  )

  const location = useLocation()
  const history = useHistory()
  const tagsResult = useQuery(GET_TAGS, {
    context: { headers: { authorization: authState.authToken } },
    fetchPolicy: 'cache-and-network',
  })

  // Arriving from a tag chip (?tag=<id>): show every voucher with that tag
  // the user can see, so clear the other saved filters before they're read.
  const [linkedTagId] = useState<number | null>(() => {
    const tagId = tagIdFromSearch(location.search)
    if (tagId !== null) {
      sessionStorage.setItem('reports_filter_tags', JSON.stringify([tagId]))
      sessionStorage.setItem('reports_filter_financial_year', 'All')
      sessionStorage.setItem(
        'reports_filter_branch',
        isNational ? 'All' : userBranch || '',
      )
      sessionStorage.setItem('reports_filter_period', 'All')
      sessionStorage.setItem(
        'reports_filter_department',
        hasFullAccess ? 'All' : userDepartmentId || 'All',
      )
      sessionStorage.setItem('reports_filter_cost_category', 'All')
      sessionStorage.setItem('reports_filter_paid_from', 'AMIN')
      sessionStorage.setItem('reports_filter_international', 'false')
      sessionStorage.setItem('reports_filter_search', '')
      const saved = sessionStorage.getItem('reports_pagination')
      const pageSize = saved ? JSON.parse(saved).pageSize : 100
      sessionStorage.setItem(
        'reports_pagination',
        JSON.stringify({ pageIndex: 0, pageSize }),
      )
    }
    return tagId
  })
  useEffect(() => {
    if (linkedTagId !== null) history.replace('/panel/reports')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(() => {
    try {
      const saved = JSON.parse(
        sessionStorage.getItem('reports_filter_tags') ?? '[]',
      )
      return Array.isArray(saved) ? saved : []
    } catch {
      return []
    }
  })

  const [selectedFinancialYear, setSelectedFinancialYear] = useState<string>(
    () => {
      const saved = sessionStorage.getItem('reports_filter_financial_year')
      return saved ?? 'All'
    },
  )
  const [selectedDepartment, setSelectedDepartment] = useState<string>(() => {
    const saved = sessionStorage.getItem('reports_filter_department')
    return saved ?? (hasFullAccess ? 'All' : userDepartmentId || 'All')
  })
  const [selectedBranch, setSelectedBranch] = useState<string>(() => {
    const saved = sessionStorage.getItem('reports_filter_branch')
    return saved ?? (isNational ? 'All' : userBranch || '')
  })
  const [selectedPeriod, setSelectedPeriod] = useState<string>(() => {
    const saved = sessionStorage.getItem('reports_filter_period')
    return saved ?? 'All'
  })
  const [selectedCostCategory, setSelectedCostCategory] = useState<string>(
    () => {
      return sessionStorage.getItem('reports_filter_cost_category') ?? 'All'
    },
  )
  const canFilterByAccount = isNational && hasFullAccess
  const [selectedPaidFrom, setSelectedPaidFrom] = useState<string>(() => {
    return sessionStorage.getItem('reports_filter_paid_from') ?? 'AMIN'
  })
  const [showInternational, setShowInternational] = useState<boolean>(() => {
    return sessionStorage.getItem('reports_filter_international') === 'true'
  })

  const [searchInput, setSearchInput] = useState<string>(() => {
    return sessionStorage.getItem('reports_filter_search') ?? ''
  })
  const [submittedSearch, setSubmittedSearch] = useState<string>(searchInput)

  const [pagination, setPagination] = useState(() => {
    const saved = sessionStorage.getItem('reports_pagination')
    return saved ? JSON.parse(saved) : { pageIndex: 0, pageSize: 100 }
  })

  const handleFinancialYearChange = (value: string) => {
    setSelectedFinancialYear(value)
    sessionStorage.setItem('reports_filter_financial_year', value)
    handlePaginationChange({ ...pagination, pageIndex: 0 })
  }

  const handleDepartmentChange = (value: string) => {
    setSelectedDepartment(value)
    sessionStorage.setItem('reports_filter_department', value)
    handlePaginationChange({ ...pagination, pageIndex: 0 })
  }

  const handleBranchChange = (value: string) => {
    setSelectedBranch(value)
    sessionStorage.setItem('reports_filter_branch', value)
    handlePaginationChange({ ...pagination, pageIndex: 0 })
  }

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value)
    sessionStorage.setItem('reports_filter_period', value)
    handlePaginationChange({ ...pagination, pageIndex: 0 })
  }

  const handleCostCategoryChange = (value: string) => {
    setSelectedCostCategory(value)
    sessionStorage.setItem('reports_filter_cost_category', value)
    handlePaginationChange({ ...pagination, pageIndex: 0 })
  }

  const handlePaidFromChange = (value: string) => {
    setSelectedPaidFrom(value)
    sessionStorage.setItem('reports_filter_paid_from', value)
    handlePaginationChange({ ...pagination, pageIndex: 0 })
  }

  const paidFromBeforeInternational = useRef('AMIN')

  const handleInternationalChange = (checked: boolean) => {
    if (checked) paidFromBeforeInternational.current = selectedPaidFrom
    const paidFrom = checked ? 'All' : paidFromBeforeInternational.current
    setSelectedPaidFrom(paidFrom)
    sessionStorage.setItem('reports_filter_paid_from', paidFrom)
    setShowInternational(checked)
    sessionStorage.setItem('reports_filter_international', String(checked))
    handlePaginationChange({ ...pagination, pageIndex: 0 })
  }

  const handleTagsChange = (ids: number[]) => {
    setSelectedTagIds(ids)
    sessionStorage.setItem('reports_filter_tags', JSON.stringify(ids))
    handlePaginationChange({ ...pagination, pageIndex: 0 })
  }

  // A saved or linked tag may since have been deleted: drop it, otherwise the
  // list would stay filtered by a tag that no longer exists.
  useEffect(() => {
    const existing = tagsResult.data?.tags
    if (!existing) return
    const kept = selectedTagIds.filter((id) =>
      existing.some((t: Tag) => t.id === id),
    )
    if (kept.length !== selectedTagIds.length) handleTagsChange(kept)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsResult.data, selectedTagIds])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
  }

  const handleSearchSubmit = () => {
    setSubmittedSearch(searchInput)
    sessionStorage.setItem('reports_filter_search', searchInput)
    handlePaginationChange({ ...pagination, pageIndex: 0 })
  }

  const handleResetFilters = () => {
    const currentFinancialYearId = findCurrentFinancialYear(
      financialYearsResult.data?.financialYears ?? [],
    )
    const financialYearValue = currentFinancialYearId
      ? String(currentFinancialYearId)
      : 'All'
    setSelectedFinancialYear(financialYearValue)
    setSelectedBranch(isNational ? 'All' : userBranch || '')
    setSelectedPeriod('All')
    setSelectedDepartment(hasFullAccess ? 'All' : userDepartmentId || 'All')
    setSelectedCostCategory('All')
    setSelectedPaidFrom('AMIN')
    setShowInternational(false)
    setSelectedTagIds([])
    setSearchInput('')
    setSubmittedSearch('')
    handlePaginationChange({ ...pagination, pageIndex: 0 })

    sessionStorage.setItem('reports_filter_financial_year', financialYearValue)
    sessionStorage.setItem(
      'reports_filter_branch',
      isNational ? 'All' : userBranch || '',
    )
    sessionStorage.setItem('reports_filter_period', 'All')
    sessionStorage.setItem(
      'reports_filter_department',
      hasFullAccess ? 'All' : userDepartmentId || 'All',
    )
    sessionStorage.setItem('reports_filter_cost_category', 'All')
    sessionStorage.setItem('reports_filter_paid_from', 'AMIN')
    sessionStorage.setItem('reports_filter_international', 'false')
    sessionStorage.setItem('reports_filter_tags', '[]')
    sessionStorage.setItem('reports_filter_search', '')
  }

  const handlePaginationChange = (nextPagination: {
    pageIndex: number
    pageSize: number
  }) => {
    setPagination(nextPagination)
    sessionStorage.setItem('reports_pagination', JSON.stringify(nextPagination))
  }

  useEffect(() => {
    // Only fills in a default on first-ever visit; an explicit saved choice
    // (including "All") is always respected.
    if (sessionStorage.getItem('reports_filter_financial_year')) return
    const currentFinancialYearId = findCurrentFinancialYear(
      financialYearsResult.data?.financialYears ?? [],
    )
    if (currentFinancialYearId) {
      handleFinancialYearChange(String(currentFinancialYearId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialYearsResult.data])

  const selectedFinancialYearRecord =
    financialYearsResult.data?.financialYears?.find(
      (fy: any) => String(fy.id) === selectedFinancialYear,
    )
  const periodOptions =
    selectedFinancialYear !== 'All' && selectedFinancialYearRecord
      ? getFinancialYearPeriods(selectedFinancialYearRecord)
      : undefined

  const queryVariables = {
    input: {
      skip: pagination.pageIndex * pagination.pageSize,
      take: pagination.pageSize,
      branch: selectedBranch !== 'All' ? selectedBranch : undefined,
      expense_period:
        selectedPeriod !== 'All' ? parseInt(selectedPeriod) : undefined,
      payment_account:
        canFilterByAccount && selectedPaidFrom !== 'All'
          ? selectedPaidFrom
          : undefined,
      international: showInternational,
      financial_year_id:
        selectedFinancialYear !== 'All'
          ? parseInt(selectedFinancialYear)
          : undefined,
      department_id:
        selectedDepartment !== 'All' ? selectedDepartment : undefined,
      cost_category_id:
        selectedCostCategory !== 'All' ? selectedCostCategory : undefined,
      tag_ids: selectedTagIds.length ? selectedTagIds : undefined,
      search: submittedSearch.trim() || undefined,
    },
  }

  const vouchers = useQuery(GET_VOUCHERS_ALL, {
    variables: queryVariables,
    context: { headers: { authorization: authState.authToken } },
    skip: !isAllowed,
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (isAllowed) {
      vouchers.refetch()
    }
  }, [
    isAllowed,
    pagination,
    selectedBranch,
    selectedPeriod,
    selectedPaidFrom,
    showInternational,
    selectedFinancialYear,
    selectedDepartment,
    selectedCostCategory,
    selectedTagIds,
    submittedSearch,
  ])

  const reportsData = vouchers.data?.vouchersAll
  const uniqueBranches = reportsData?.uniqueBranches ?? []
  const uniquePeriods = periodOptions ?? reportsData?.uniquePeriods ?? []

  const formatSEK = (amount: number) =>
    new Intl.NumberFormat('en-SE', {
      style: 'currency',
      currency: 'SEK',
    }).format(amount)
  const categoryAmount: number | null = reportsData?.categoryAmount ?? null

  const columns = React.useMemo(
    () =>
      getReportsColumns(isNationalMaal, (tag: Tag) =>
        handleTagsChange([tag.id]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isNationalMaal],
  )

  if (!isAllowed) {
    return <Redirect to="/panel" />
  }

  if (vouchers.loading && !vouchers.data) return <Loader />
  if (vouchers.error) return <p>Error: {vouchers.error.message}</p>

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="Reports" icon={AssessmentIcon} />
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <Grid container spacing={2} sx={{ alignItems: 'flex-end' }}>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Filter by Financial Year"
              value={selectedFinancialYear}
              onChange={(e) => handleFinancialYearChange(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {financialYearsResult.data?.financialYears?.map((fy: any) => (
                <MenuItem key={fy.id} value={fy.id}>
                  {fy.year}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Filter by Branch"
              value={isNational ? selectedBranch : userBranch}
              onChange={(e) => isNational && handleBranchChange(e.target.value)}
              disabled={!isNational}
            >
              {isNational && <MenuItem value="All">All</MenuItem>}
              {isNational ? (
                uniqueBranches.map((branch: any) => (
                  <MenuItem key={branch} value={branch}>
                    {branch}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={userBranch}>{userBranch}</MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Filter by Period"
              value={selectedPeriod}
              onChange={(e) => handlePeriodChange(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {uniquePeriods.map((period: any) => (
                <MenuItem key={period} value={period}>
                  {period}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Filter by Department"
              value={hasFullAccess ? selectedDepartment : userDepartmentId}
              onChange={(e) =>
                hasFullAccess && handleDepartmentChange(e.target.value)
              }
              disabled={!hasFullAccess}
            >
              {hasFullAccess && <MenuItem value="All">All</MenuItem>}
              {hasFullAccess ? (
                (isNational
                  ? departmentsResult.data?.departments
                  : departmentsResult.data?.departments?.filter(
                      (dept: any) => !dept.national_only,
                    )
                )?.map((dept: any) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={userDepartmentId}>
                  {authState.authUser.department?.name}
                </MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Filter by Cost Category"
              value={selectedCostCategory}
              onChange={(e) => handleCostCategoryChange(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {sortedCostCategories.map((category: any) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TagFilter
              options={tagsResult.data?.tags ?? []}
              selectedIds={selectedTagIds}
              onChange={handleTagsChange}
            />
          </Grid>
          {canFilterByAccount && (
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Paid from"
                value={selectedPaidFrom}
                onChange={(e) => handlePaidFromChange(e.target.value)}
              >
                <MenuItem value="All">All</MenuItem>
                {(Object.keys(PAYMENT_ACCOUNT_LABELS) as PaymentAccount[]).map(
                  (account) => (
                    <MenuItem key={account} value={account}>
                      {PAYMENT_ACCOUNT_LABELS[account]}
                    </MenuItem>
                  ),
                )}
              </TextField>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInternational}
                  onChange={(e) => handleInternationalChange(e.target.checked)}
                />
              }
              label="International"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              onClick={handleResetFilters}
            >
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Box>
      <div style={{ overflowX: 'auto' }}>
        <DataTable
          data={reportsData?.vouchers ?? []}
          columns={columns}
          manualPagination={true}
          pageCount={Math.ceil(
            (reportsData?.totalCount ?? 0) / pagination.pageSize,
          )}
          totalCount={reportsData?.totalCount ?? 0}
          onPaginationChange={handlePaginationChange}
          initialPagination={pagination}
          globalFilter={searchInput}
          onGlobalFilterChange={handleSearchChange}
          showSearchButton={true}
          onSearchSubmit={handleSearchSubmit}
          toolbarEnd={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 100,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.07),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.15),
                whiteSpace: 'nowrap',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 500 }}
              >
                {categoryAmount !== null ? 'Category share / Total' : 'Total'}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: 'primary.dark' }}>
                {categoryAmount !== null
                  ? `${formatSEK(categoryAmount)} / ${formatSEK(reportsData?.totalAmount ?? 0)}`
                  : formatSEK(reportsData?.totalAmount ?? 0)}
              </Typography>
              {!!reportsData?.foreignCurrencyCount && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {reportsData.foreignCurrencyCount === 1
                    ? '1 voucher in foreign currency not included'
                    : `${reportsData.foreignCurrencyCount} vouchers in foreign currency not included`}
                </Typography>
              )}
            </Box>
          }
          renderSubComponent={(row) => (
            <VoucherExpandedRow voucher={row.original} showPaymentInfo={true} />
          )}
        />
      </div>
    </Box>
  )
}

export default Reports
